/**
 * SelfServiceCompleteCard — vervangt bij zelf brengen/halen de ophaal- of
 * afleversectie op het order-detail. Die secties gaan over ritten, adressen en
 * afspraken; bij een walk-in bestaat daar niets van.
 *
 *  - side='brengen' (Zelf_brengen__c) staat op de plek van "Ophalen"
 *  - side='halen'   (Zelf_halen__c)   staat op de plek van "Afleveren"
 *
 * De kaart toont per kant één van drie toestanden:
 *  1. geregistreerd  → wanneer de klant het bracht of ophaalde
 *  2. nu aan de beurt → datumkeuze + registratieknop
 *       'Wordt gebracht'  → 'Zelf gebracht' + items klaar voor voorinspectie
 *       'Wordt opgehaald' → 'Afgerond'      + items op Afgeleverd
 *  3. nog niet aan de beurt → korte uitleg wat er straks moet gebeuren
 *
 * De registratieknop roept POST /api/orders/self-service-complete aan, dat de
 * Apex-flow draait (status + wasserij-items + datum). De datum is instelbaar
 * zodat achteraf vastleggen kan.
 */

import { useState } from 'react';
import type { OrderDetail } from '../../lib/orders';
import { completeSelfService } from '../../lib/orders';

interface Props {
  order: OrderDetail;
  /** Welke kant deze kaart vervangt: het brengen (ophaal) of het halen (aflever). */
  side: 'brengen' | 'halen';
  onCompleted: () => void;
}

/** Vandaag als yyyy-MM-dd in lokale tijd (voor het date-input + backend). */
function todayIso(): string {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function formatDatum(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function SelfServiceCompleteCard({ order, side, onCompleted }: Props) {
  const isPickup = side === 'halen';

  const [datum, setDatum] = useState<string>(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Al geregistreerd? Dan is de datum leidend, ongeacht waar de order inmiddels staat.
  const geregistreerdOp = formatDatum(isPickup ? order.selfPickedUpAt : order.selfDeliveredAt);
  // Aan de beurt: de status waarop deze kant geregistreerd kan worden.
  const actieStatus = isPickup ? 'Wordt opgehaald' : 'Wordt gebracht';
  const isActief = !geregistreerdOp && order.status === actieStatus;

  const titel = isPickup ? 'Zelf halen' : 'Zelf brengen';
  const actieLabel = isPickup ? 'Markeer als opgehaald' : 'Markeer als gebracht';
  const nieuweStatus = isPickup ? 'Afgerond' : 'Zelf gebracht';

  const handleComplete = async () => {
    setBusy(true);
    setError(null);
    try {
      await completeSelfService(order.id, datum);
      onCompleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registreren mislukt');
    } finally {
      setBusy(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{titel}</h2>
        {geregistreerdOp ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
            {isPickup ? 'Opgehaald' : 'Gebracht'}
          </span>
        ) : (
          isActief && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700 border border-blue-200">
              {order.status}
            </span>
          )
        )}
      </div>

      {geregistreerdOp ? (
        <>
          <p className="text-sm text-slate-900 leading-snug">
            {isPickup ? 'Opgehaald op' : 'Gebracht op'} {geregistreerdOp}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {isPickup
              ? 'De klant heeft de schone kleden zelf opgehaald.'
              : 'De klant heeft de kleden zelf langsgebracht.'}
          </p>
        </>
      ) : isActief ? (
        <>
          <p className="text-sm text-slate-600 mb-3 leading-snug">
            {isPickup
              ? 'De klant haalt de schone kleden zelf op. Na registratie gaat de order naar Afgerond en worden de wasserij-items op Afgeleverd gezet.'
              : 'De klant brengt de kleden zelf langs. Na registratie gaat de order naar Zelf gebracht en worden de wasserij-items klaargezet voor voorinspectie.'}
          </p>

          {error && (
            <div className="text-xs px-2 py-1.5 mb-3 rounded bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}

          <div className="flex items-end gap-3 flex-wrap">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-500">
                {isPickup ? 'Opgehaald op' : 'Gebracht op'}
              </span>
              <input
                type="date"
                value={datum}
                max={todayIso()}
                onChange={(e) => setDatum(e.target.value)}
                disabled={busy}
                className="px-2 py-1.5 text-sm rounded-md border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>

            {!showConfirm ? (
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={busy || !datum}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actieLabel}
              </button>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-200 bg-indigo-50">
                <span className="text-sm text-slate-700">
                  Order naar <code>{nieuweStatus}</code>?
                </span>
                <button
                  type="button"
                  onClick={handleComplete}
                  disabled={busy}
                  className="px-2 py-1 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Bezig…' : 'Ja, registreer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  disabled={busy}
                  className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
                >
                  Annuleer
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600 leading-snug">
            {isPickup
              ? 'De klant haalt de schone kleden zelf op. Geen bezorgrit.'
              : 'De klant brengt de kleden zelf langs. Geen ophaalrit.'}
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Registreren kan zodra de order op '{actieStatus}' staat.
          </p>
        </>
      )}
    </div>
  );
}
